import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { TrendUp, GitDiff, Globe, FileText, ArrowRight } from "@phosphor-icons/react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function TendancesSection() {
  const [data, setData] = useState(null);

  useEffect(() => {
    fetch(`${BACKEND}/api/public/observatory/tendances`)
      .then((r) => r.json())
      .then(setData)
      .catch(() => {});
  }, []);

  const stats = data?.stats || {};
  const divergences = data?.divergences_recentes || [];

  return (
    <section className="border-b border-black bg-[var(--paper-2)]" data-testid="tendances-section">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-20">
        <div className="grid grid-cols-12 gap-8 mb-12">
          <div className="col-span-12 lg:col-span-4">
            <div className="label-mono">§ 03·bis · Tendances 2026</div>
            <h2 className="mt-3 font-serif text-3xl sm:text-5xl font-medium tracking-tight leading-tight">
              Vitrine éditoriale.<br/><span className="italic text-[var(--signal)]">En temps réel.</span>
            </h2>
            <p className="mt-6 text-[var(--muted)] leading-relaxed">
              VITA-OBSERVATORY agrège les rapports flagship du FMI, de la Banque Mondiale, de la BAD et de la CEA.
              L'IA juridique extrait les divergences chiffrées et les met en regard.
            </p>
          </div>

          <div className="col-span-12 lg:col-span-8">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-px bg-black/15 border border-black/15">
              <Stat icon={FileText} value={stats.rapports_collectes ?? "—"} label="Rapports collectés" />
              <Stat icon={TrendUp} value={stats.analyses_produites ?? "—"} label="Analyses IA produites" />
              <Stat icon={GitDiff} value={stats.comparaisons_croisees ?? "—"} label="Comparaisons croisées" />
              <Stat icon={Globe} value={stats.pays_couverts ?? "—"} label="Pays couverts" />
              <Stat icon={Globe} value={stats.organisations_actives ?? "—"} label="Organisations actives" />
              <Stat icon={FileText} value={stats.dvc_publiques ?? "—"} label="DVC publiées" accent />
            </div>
          </div>
        </div>

        {/* Divergences */}
        <div className="border border-black bg-[var(--paper)]">
          <div className="border-b border-black p-6 flex items-center justify-between">
            <div>
              <div className="label-mono">§ Divergences récentes entre sources</div>
              <h3 className="font-serif text-2xl mt-1">Quand FMI ≠ BAD ≠ Banque Mondiale</h3>
            </div>
            <Link to="/auth" data-testid="tendances-cta-explore" className="border border-black px-4 py-2 label-mono hover:bg-black hover:text-[var(--paper)] hidden md:flex items-center gap-2">
              Explorer <ArrowRight size={14} />
            </Link>
          </div>

          {divergences.length === 0 ? (
            <div className="p-12 text-center">
              <div className="label-mono text-[var(--muted)]">En attente d'analyses</div>
              <p className="mt-3 font-serif text-2xl text-[var(--muted)] max-w-2xl mx-auto">
                Les premières divergences apparaîtront ici dès que vos analyses IA seront produites.
              </p>
              <Link to="/auth" data-testid="tendances-cta-empty"
                className="inline-flex mt-6 bg-[var(--signal)] hover:bg-[var(--signal-hover)] text-[var(--paper)] px-5 py-3 label-mono items-center gap-2">
                Importer le premier rapport <ArrowRight size={14} weight="bold" />
              </Link>
            </div>
          ) : (
            <div className="divide-y divide-black/10">
              {divergences.map((d, i) => (
                <div key={i} className="p-6">
                  <div className="flex items-start justify-between flex-wrap gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="label-mono">Indicateur</div>
                      <div className="mt-1 font-serif text-xl">{d.indicateur}</div>
                      {d.interpretation && (
                        <p className="mt-3 text-[var(--muted)] italic max-w-2xl">{d.interpretation}</p>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {(d.sources || []).map((s, j) => (
                        <div key={j} className="border border-black/30 px-3 py-2 bg-[var(--paper-2)]">
                          <div className="label-mono text-[9px]">{s.org}</div>
                          <div className="font-serif text-2xl tabular-nums">{s.valeur}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function Stat({ icon: Icon, value, label, accent }) {
  return (
    <div className={`p-5 ${accent ? "bg-[var(--signal-light)]" : "bg-[var(--paper)]"}`}>
      <Icon size={18} className="text-[var(--signal)]" />
      <div className="mt-2 text-3xl sm:text-4xl font-light tabular-nums">{value}</div>
      <div className="label-mono mt-1">{label}</div>
    </div>
  );
}
